import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  getAlsoLikedProducts,
  getFilteredProducts,
  getHomeProducts,
  getNewArrivals,
  getProduct,
  getProductFilters,
  getProducts,
} from '../../api/products';
import { sagaTypes } from './types';
import {
  getMoreProductsAction,
  getProductAction,
  setSizePageAction,
  getProductsAction,
  getFiltersAction,
  getAlsoLikedProductsAction,
  getHomeProductsAction,
  getInitialFiltersAction,
} from './action';
import { IData, IDataPage } from '../../interfaces/common';
import { IProduct } from '../../interfaces/product';
import { IAction } from '../utils';
import { FilterItemType, FilterType } from '../../react-pages/Catalog/types';
import { v4 as uuid } from 'uuid';
import { getCollectionsProductFilters } from '../../api/products';

export function* getHomeProductsWorkerSaga({ payload }: IAction<string>) {
  const res: IDataPage<IProduct[]> = yield call(getHomeProducts, payload as string);
  yield put(getHomeProductsAction(res?.data?.items || []));
}

export function* getProductsWorkerSaga({ payload }: IAction<number>) {
  const res: IDataPage<IProduct[]> = yield call(getProducts, payload);
  yield put(getProductsAction(res?.data?.items || []));
  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getMoreProductsWorkerSaga({ payload }: IAction<number>) {
  const res: IDataPage<IProduct[]> = yield call(getProducts, payload);
  yield put(getMoreProductsAction(res?.data?.items || []));
  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getNewArrivalsWorkerSaga({ payload }: IAction<number>) {
  const res: IDataPage<IProduct[]> = yield call(getNewArrivals, payload);
  yield put(getProductsAction(res?.data?.items || []));
  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getMoreNewArrivalsWorkerSaga({ payload }: IAction<number>) {
  const res: IDataPage<IProduct[]> = yield call(getNewArrivals, payload);

  yield put(getMoreProductsAction(res?.data?.items || []));

  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getFilteredProductsWorkerSaga({ payload }: IAction<string>) {
  const res: IDataPage<IProduct[]> = yield call(getFilteredProducts, payload as string);
  yield put(getProductsAction(res?.data?.items || []));
  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getMoreFilteredProductsWorkerSaga({ payload }: IAction<string>) {
  const res: IDataPage<IProduct[]> = yield call(getFilteredProducts, payload as string);
  yield put(getMoreProductsAction(res?.data?.items || []));
  if (res?.data) {
    const { pageSize, totalPages, totalCount, pageIndex } = res?.data;
    yield put(setSizePageAction({ pageSize, totalPages, totalCount, pageIndex }));
  }
}

export function* getAlsoLikedProductsWorkerSaga({ payload }: IAction<string>) {
  const res: { data: IProduct[] } = yield call(getAlsoLikedProducts, payload as string);
  yield put(getAlsoLikedProductsAction(res?.data || []));
}

export function* getProductWorkerSaga({ payload }: IAction<string>) {
  const res: IDataPage<IProduct[]> = yield call(getProduct, payload as string);
  yield put(getProductAction(res?.data?.items?.[0]));
}

export function* getProductByIdWorkerSaga({ payload }: IAction<string>) {
  const res: IData<IProduct> = yield call(getProduct, payload as string);
  yield put(getProductAction(res?.data));
}

export function* getFiltersWorkerSaga({ payload }: IAction<string>) {
  const res: IData<FilterType[]> = yield call(getProductFilters, payload as string);
  const storeFilters: FilterType[] = yield select((state) => state.products.filters);
  const data = res?.data?.map((item: FilterType) => ({
    ...item,
    filters: item?.filters?.map((fItem) => ({
      ...fItem,
      id: uuid(),
      checked: storeFilters?.length
        ? storeFilters
            ?.find((filter: FilterType) => filter?.columnName === item?.columnName)
            ?.filters?.find((filterItem: FilterItemType) => filterItem?.key === fItem?.key)?.checked || false
        : false,
    })),
  }));
  yield put(getFiltersAction(data || []));
  if (!payload) {
    yield put(getInitialFiltersAction(data || []));
  }
}

export function* getCollectionsFiltersWorkerSaga({ payload, groupId }: IAction<string>) {
  const res: IData<FilterType[]> = yield call(getCollectionsProductFilters, payload as string, groupId as string);
  const storeFilters: FilterType[] = yield select((state) => state.products.filters);
  const data = res?.data?.map((item: FilterType) => ({
    ...item,
    filters: item?.filters?.map((fItem) => ({
      ...fItem,
      id: uuid(),
      checked: storeFilters?.length
        ? storeFilters
            ?.find((filter: FilterType) => filter?.columnName === item?.columnName)
            ?.filters?.find((filterItem: FilterItemType) => filterItem?.key === fItem?.key)?.checked || false
        : false,
    })),
  }));
  yield put(getFiltersAction(data || []));
  if (!payload) {
    yield put(getInitialFiltersAction(data || []));
  }
}

export function* productsWatcherSaga() {
  yield takeEvery(sagaTypes?.GET_HOME_PRODUCTS, getHomeProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_ALL_PRODUCTS, getProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_MORE_PRODUCTS, getMoreProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_NEW_ARRIVALS, getNewArrivalsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_MORE_NEW_ARRIVALS, getMoreNewArrivalsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_FILTERED_PRODUCTS, getFilteredProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_MORE_FILTERED_PRODUCTS, getMoreFilteredProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_ALSO_LIKED_PRODUCTS, getAlsoLikedProductsWorkerSaga);
  yield takeEvery(sagaTypes?.GET_PRODUCT, getProductWorkerSaga);
  yield takeEvery(sagaTypes?.GET_PRODUCT_BY_ID, getProductByIdWorkerSaga);
  yield takeEvery(sagaTypes?.GET_FILTERS, getFiltersWorkerSaga);
  yield takeEvery(sagaTypes?.GET_COLLECTIONS_FILTERS, getCollectionsFiltersWorkerSaga);
}
